import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react';
import { GlobalContext } from 'context/userContext';

import { Grid, Paper, Typography, Box, useTheme } from '@mui/material';
import { DrawerHeader } from '@components/drawer-header';
import DateSelector from '@components/date-selector';
import DropBoxSelector from '@components/dropbox-selector';
import TableExplorer from '@components/table-explorer/table_explorer';
import * as S from './styles';
import { Button, Stack, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAllQuestionsMap } from '@pages/checklist-config/api';
import { AxiosInstance } from '@service/axios-instance';


export function ChecklistReports() {

  const { operation } = useContext(GlobalContext);
  const theme = useTheme();

  // Date selection state (default last 30 days)
  const defaultEndDate = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 30);
  const [localStartDate, setLocalStartDate] = useState<Date>(defaultStartDate);
  const [localEndDate, setLocalEndDate] = useState<Date>(defaultEndDate);

  const handleStartDateChange = (date: Date) => {
    if (!date) return;
    const diffTime = localEndDate.getTime() - date.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 90) {
      alert("O limite máximo de busca é de 90 dias por vez. Selecione um periodo mais curto");
      return;
    }
    if (date > localEndDate) {
      alert("A data inicial não pode ser maior que a final.");
      return;
    }
    setLocalStartDate(date);
  };

  const handleEndDateChange = (date: Date) => {
    if (!date) return;
    const diffTime = date.getTime() - localStartDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 90) {
      alert("O limite máximo de busca é de 90 dias por vez. Selecione um periodo mais curto.");
      return;
    }
    if (date < localStartDate) {
      alert("A data final não pode ser menor que a inicial.");
      return;
    }
    setLocalEndDate(date);
  };

  const formattedStart = (() => {
    const d = new Date(localStartDate.getTime());
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  })();

  const formattedEnd = (() => {
    const d = new Date(localEndDate.getTime());
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  })();

  const [questionsMap, setQuestionsMap] = useState<Record<string, { text: string; answers: { value: string; label: string }[] }[]>>({});
  const [usersMap, setUsersMap] = useState<Record<string, string>>({});
  const [mapLoaded, setMapLoaded] = useState(false);
  const [metrics, setMetrics] = useState({
    total: 0,
    completed: 0,
    avgDuration: 0,
    avgQuestions: 0,
    blocked: 0,
    incompletes: 0,
  });

  useEffect(() => {
    if (operation) {
      Promise.all([
        getAllQuestionsMap(operation),
        AxiosInstance.post(`/users-machine/get_users-machine?db=${encodeURIComponent(operation)}`, {}, { withCredentials: true })
      ])
        .then(([qMap, uRes]) => {
          setQuestionsMap(qMap);

          const uMap: Record<string, string> = {};
          if (uRes.data && Array.isArray(uRes.data)) {
            uRes.data.forEach((u: any) => {
              if (u.login !== undefined && u.login !== null && u.login !== "") {
                const loginStr = u.login.toString().trim().toLowerCase();
                uMap[loginStr] = u.name || "";
                if (loginStr !== "" && !isNaN(Number(u.login))) {
                  uMap[Number(u.login).toString()] = u.name || "";
                }
              }
              if (u.rfid_uuid !== undefined && u.rfid_uuid !== null && u.rfid_uuid !== "") {
                const rfidStr = u.rfid_uuid.toString().trim().toLowerCase();
                uMap[rfidStr] = u.name || "";
                if (rfidStr !== "" && !isNaN(Number(u.rfid_uuid))) {
                  uMap[Number(u.rfid_uuid).toString()] = u.name || "";
                }
              }
            });
          }
          console.log("uRes.data:", uRes.data);
          console.log("usersMap generated:", uMap);
          setUsersMap(uMap);
          setMapLoaded(true);
        })
        .catch((e) => {
          console.error(e);
          setMapLoaded(true);
        });
    }
  }, [operation]);

  /*defined at tcp_udp.h*/
  const mapStatus = (status: number) => {
    switch (status) {
      case 6: return "Incompleto";
      case 7: return "Alerta";
      case 8: return "Bloqueado";
      case 9: return "Aprovado";
      default: return `Desconhecido (${status})`;
    }
  };

  return (
    <DrawerHeader
      title={"Checklists"}
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2.5, md: 3 },
              bgcolor: 'background.paper',
              borderLeft: `6px solid ${theme.palette.primary.main}`,
              borderRadius: 2,
              mb: 1
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={3}>
              <Box>
                <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Histórico de Checklists</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  Consulte o histórico de inspeções pré-operacionais realizadas pelos operadores. Acompanhe a conformidade técnica e falhas relatadas.
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'background.default',
                p: 2,
                pt: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mr: 1, textTransform: 'uppercase' }}>
                  Período
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <DateSelector
                    label="Data Inicial"
                    value={localStartDate}
                    onDateSelect={handleStartDateChange}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>até</Typography>
                  <DateSelector
                    label="Data Final"
                    value={localEndDate}
                    onDateSelect={handleEndDateChange}
                  />
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Checklists Realizados</Typography>
              <Typography variant="h5" fontWeight="bold">{metrics.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Completados</Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">{metrics.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Tempo Médio (s)</Typography>
              <Typography variant="h5" fontWeight="bold">{metrics.avgDuration}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Perguntas em Média</Typography>
              <Typography variant="h5" fontWeight="bold">{metrics.avgQuestions}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Bloqueados</Typography>
              <Typography variant="h5" fontWeight="bold" color="error.main">{metrics.blocked}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Incompletos (Timeout)</Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">{metrics.incompletes}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {
        mapLoaded && (
          <Box sx={{
            '& table.dataTable th': {
              verticalAlign: 'bottom',
              textAlign: 'center !important',
            },
            '& table.dataTable td': {
              textAlign: 'center !important',
            },
            '& table.dataTable td:nth-of-type(6), & table.dataTable th:nth-of-type(6)': {
              borderRight: `2px solid ${theme.palette.divider}`,
            }
          }}>
            <TableExplorer
              key={`${formattedStart}-${formattedEnd}`}
              dbName={operation}
              tableName={"checklists"}
              startDate={formattedStart}
              endDate={formattedEnd}
              dataProcessor_cb={(data) => {
                const total = data.length;
                let completed = 0;
                let sumDuration = 0;
                let sumQuestions = 0;
                let blocked = 0;
                let warn = 0;
                let incompletes = 0;

                const processed = data.map((row: any) => {
                  if (row.status === 9) completed++;
                  if (row.status === 8) blocked++;
                  if (row.status === 7) warn++;
                  if (row.status === 6) incompletes++;

                  sumDuration += Number(row.duration) || 0;

                  const qorder = (row.qorder || "").toString().split(",").filter((q: string) => q.trim() !== "");
                  const answers = (row.answers || "").toString().split(",");
                  sumQuestions += qorder.length;

                  const userIdStr = (row.user !== undefined && row.user !== null) ? row.user.toString().trim().toLowerCase() : "";
                  const userIdStrNumeric = !isNaN(Number(row.user)) ? Number(row.user).toString() : "";
                  const userName = usersMap[userIdStr] || usersMap[userIdStrNumeric] || "";

                  const newRow: any = {
                    stamp: row.stamp || "",
                    user: (row.user !== undefined && row.user !== null) ? row.user.toString() : "",
                    name: userName,
                    id: row.id || "",
                    duration: row.duration || "",
                    status: mapStatus(row.status),
                  };

                  qorder.forEach((q: string, idx: number) => {
                    const qid = q.trim();
                    if (qid !== "") {
                      const checklistId = row.checklist_id || "";
                      const qIndex = parseInt(qid);

                      const qInfo = (questionsMap[checklistId] && questionsMap[checklistId][qIndex])
                        ? questionsMap[checklistId][qIndex]
                        : null;

                      const headerName = qInfo ? qInfo.text : `Q: ${qid}`;
                      let answerVal = answers[idx] !== undefined ? answers[idx] : "";

                      // Try to find the label for this answer
                      if (qInfo && qInfo.answers) {
                        const foundOption = qInfo.answers.find((a: any) => a.value === answerVal);
                        if (foundOption) {
                          answerVal = foundOption.label;
                        }
                      }

                      newRow[headerName] = answerVal;
                    }
                  });

                  return newRow;
                });

                // Set metrics async to not block render
                setTimeout(() => {
                  setMetrics({
                    total,
                    completed,
                    avgDuration: total > 0 ? Math.round(sumDuration / total) : 0,
                    avgQuestions: total > 0 ? Math.round(sumQuestions / total) : 0,
                    blocked,
                    incompletes,
                  });
                }, 0);

                return processed;
              }}
            />
          </Box>
        )
      }
    </DrawerHeader>
  );
}
